// Static demo-mode fill-in data for UrlTracking.component.jsx.
//
// UrlTracking reads the same `state.timeline.sessionsByDay` slice as Timeline
// (populated by the same GET_SESSIONS action), but only ever renders the
// first day bucket (`sessionsByDay?.[0]`) since the page is scoped to a
// single selected date. On top of Timeline's session shape, this page's
// DomainUsageTable.component.jsx reads `session.urlUsage` — a
// `{ [domain]: seconds }` map — to render the per-domain breakdown
// (DomainUsageTableComponent's `Object.entries(session.urlUsage || {})`).
// Timeline's own demo sessions don't carry that field, so this view gets its
// own fixture with `urlUsage` added per session.
//
// SessionAccordionCard additionally reads `session.start`/`session.end`
// (accordion heading time range), `session.durationSec` (total duration
// label), and `session.pastData`/`session.typeLog` (SessionSourceTags
// badges) — all included below to match Timeline's per-session shape.
// Anchored to "today" (UTC) so the demo never shows a stale fixed date —
// mirrors Timeline.demoData.js's day-0 bucket, since this view reads the
// same underlying sessionsByDay slice (see file header). activityPct/
// earnings match that same day-0 math: (duration-idle)/duration and
// productive-hours x $45/hr.
const today = new Date();
const pad = (n) => String(n).padStart(2, '0');
const TODAY_KEY = `${today.getUTCFullYear()}-${pad(today.getUTCMonth() + 1)}-${pad(today.getUTCDate())}`;
const TODAY_LABEL = today.toLocaleDateString('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export const DEMO_SESSIONS_BY_DAY = [
  {
    dayKey: TODAY_KEY,
    dayLabel: TODAY_LABEL,
    totals: {
      durationSec: 27000,
      idleSec: 2100,
      productiveSec: 24900,
      activityPct: 92.22,
      earnings: 311.25,
    },
    sessions: [
      {
        id: 'demo-url-session-1',
        start: `${TODAY_KEY}T09:02:00Z`,
        end: `${TODAY_KEY}T12:47:00Z`,
        durationSec: 13500,
        idleSec: 900,
        productiveSec: 12600,
        activityPct: 93,
        pastData: false,
        typeLog: 'auto',
        urlUsage: {
          'github.com': 4200,
          'docs.google.com': 3100,
          'stackoverflow.com': 2400,
          'slack.com': 2100,
          'mail.google.com': 1200,
          'youtube.com': 500,
        },
      },
      {
        id: 'demo-url-session-2',
        start: `${TODAY_KEY}T13:30:00Z`,
        end: `${TODAY_KEY}T17:15:00Z`,
        durationSec: 13500,
        idleSec: 1200,
        productiveSec: 12300,
        activityPct: 91,
        pastData: false,
        typeLog: 'auto',
        urlUsage: {
          'figma.com': 4800,
          'notion.so': 3300,
          'github.com': 2600,
          'zoom.us': 1900,
          'linkedin.com': 700,
        },
      },
    ],
  },
];

// Keyed by session id (must match DEMO_SESSIONS_BY_DAY session ids above).
// DomainUsageTable.component.jsx expands a domain row into individual URLs
// fetched via useSessionUrlActivities, which reads real Firestore data — in
// demo mode there's no such document, so expansion would otherwise show "No
// URL-level data available". This fixture matches fetchSessionUrlActivities'
// return shape exactly ({ url, domain, title, totalSeconds }, in
// urlTrackingService.js) so DomainUsageTable can render it in place of a
// real fetch. Every domain key from each session's urlUsage above has at
// least one matching entry here.
export const DEMO_SESSION_URL_ACTIVITIES = {
  'demo-url-session-1': [
    {
      url: 'https://github.com/meridianlabs/webapp/pull/482',
      domain: 'github.com',
      title: 'Add checkout retry logic · Pull Request #482',
      totalSeconds: 2400,
    },
    {
      url: 'https://github.com/meridianlabs/webapp/issues/317',
      domain: 'github.com',
      title: 'Fix gallery placeholder images #317',
      totalSeconds: 1800,
    },
    {
      url: 'https://docs.google.com/document/d/1a2b3c/edit',
      domain: 'docs.google.com',
      title: 'Q3 Product Roadmap',
      totalSeconds: 1900,
    },
    {
      url: 'https://docs.google.com/spreadsheets/d/9x8y7z/edit',
      domain: 'docs.google.com',
      title: 'Sprint Capacity Planning',
      totalSeconds: 1200,
    },
    {
      url: 'https://stackoverflow.com/questions/12345678/react-query-stale-time',
      domain: 'stackoverflow.com',
      title:
        'How does staleTime interact with the enabled flag? - Stack Overflow',
      totalSeconds: 1500,
    },
    {
      url: 'https://stackoverflow.com/questions/22334455/firestore-nested-collection-query',
      domain: 'stackoverflow.com',
      title: 'Querying nested Firestore subcollections efficiently',
      totalSeconds: 900,
    },
    {
      url: 'https://app.slack.com/client/T123/C456',
      domain: 'slack.com',
      title: '#eng-frontend | Meridian Labs Slack',
      totalSeconds: 2100,
    },
    {
      url: 'https://mail.google.com/mail/u/0/#inbox',
      domain: 'mail.google.com',
      title: 'Inbox (14) - hello@meridianlabs.com - Gmail',
      totalSeconds: 1200,
    },
    {
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      domain: 'youtube.com',
      title: 'React Query v5 Migration Guide - YouTube',
      totalSeconds: 500,
    },
  ],
  'demo-url-session-2': [
    {
      url: 'https://www.figma.com/file/abc123/Meridian-Design-System',
      domain: 'figma.com',
      title: 'Meridian Design System – Figma',
      totalSeconds: 3000,
    },
    {
      url: 'https://www.figma.com/file/def456/Checkout-Redesign',
      domain: 'figma.com',
      title: 'Checkout Redesign – Figma',
      totalSeconds: 1800,
    },
    {
      url: 'https://www.notion.so/meridianlabs/Engineering-Wiki-9f8e7d',
      domain: 'notion.so',
      title: 'Engineering Wiki – Notion',
      totalSeconds: 2000,
    },
    {
      url: 'https://www.notion.so/meridianlabs/Sprint-42-Retro-1a2b3c',
      domain: 'notion.so',
      title: 'Sprint 42 Retro – Notion',
      totalSeconds: 1300,
    },
    {
      url: 'https://github.com/meridianlabs/webapp/pull/489',
      domain: 'github.com',
      title: 'Fix checkout expand animation · Pull Request #489',
      totalSeconds: 2600,
    },
    {
      url: 'https://zoom.us/j/98765432100',
      domain: 'zoom.us',
      title: 'Design Review – Zoom',
      totalSeconds: 1900,
    },
    {
      url: 'https://www.linkedin.com/company/meridian-labs/',
      domain: 'linkedin.com',
      title: 'Meridian Labs | LinkedIn',
      totalSeconds: 700,
    },
  ],
};
